'use client'

import { RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import type { FuelEstimate, FuelType, VehicleId, Waypoint } from '@/lib/types'
import { DonateButton } from './donate-button'
import { PdfExport } from './pdf-export'
import { PriceTicker } from './price-ticker'
import { ShareButton } from './share-button'

/**
 * Sticky top bar — the live price ticker plus a slim brand/action row, pinned
 * to the top while the rest of the page scrolls underneath. The personal logo
 * sits on the left at every width; share / export / reset anchor right and only
 * appear once there's something to act on.
 *
 * The three hero CTA cards (how-it-works · welcome+logo · roadmap) were removed
 * 2026-05-31 — they were largely redundant and ate above-the-fold space; the
 * form now starts much higher, especially on mobile.
 */

export interface EstimatorHeaderProps {
	origin: Waypoint | null
	destination: Waypoint | null
	waypoints: Waypoint[]
	vehicleId: VehicleId
	fuelType: FuelType
	customMpg: number | null
	estimate: FuelEstimate | null
	/** Fired when the user clicks Reset. */
	onReset: () => void
}

export function EstimatorHeader({
	origin,
	destination,
	waypoints,
	vehicleId,
	fuelType,
	customMpg,
	estimate,
	onReset,
}: EstimatorHeaderProps) {
	const hasAnyInput = !!origin || !!destination || !!estimate

	// Soft shadow under the sticky bar appears once the page is scrolled,
	// separating it from the content sliding underneath.
	const [scrolled, setScrolled] = useState(false)
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 4)
		onScroll()
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [])

	// Reduced-motion guard for the animated logo. CSS prefers-reduced-motion
	// cannot stop <video> playback, so honor the preference imperatively:
	// pause on mount when the user opts out of motion, letting the poster
	// (/images/logo-poster.jpg) stand in as a still frame. autoPlay stays on
	// the element so users without the preference still get the animation.
	const videoRef = useRef<HTMLVideoElement>(null)
	useEffect(() => {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			videoRef.current?.pause()
		}
	}, [])

	return (
		<div
			className={`sticky top-0 z-40 bg-background/80 backdrop-blur-md transition-shadow duration-300 ${scrolled ? 'shadow-[0_8px_24px_-6px_rgba(0,0,0,0.6)]' : ''}`}
		>
			{/* Live pricing ticker — shares the Redis-warmed EIA cache, so first
			    paint is effectively instant. */}
			<header className="border-b border-border bg-surface/80 safe-top">
				<PriceTicker fuelType={fuelType} />
			</header>

			{/* Brand + actions row — logo pinned left; share / export / reset on the
			    right, shown only once there's something to act on. */}
			<div className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 pb-2 pt-2 sm:px-6">
				{/* Personal brand mark — Chris Lee Bergstrom animated logo, edge-faded.
				    Wrapped in a link to the personal site (cross-property backlink);
				    mr-auto pins it to the left while share / export / reset stay right.
				    target=_blank keeps any in-progress estimate intact; rel="noopener"
				    blocks tab-nabbing while still passing the referrer so the
				    destination's analytics can attribute the traffic. The sr-only span
				    is the link's screen-reader text (the video carries no accessible
				    name), and `title` drives the on-hover tooltip for sighted users. */}
				<a
					href="https://chrisleebergstrom.com"
					target="_blank"
					rel="noopener"
					title="Chris Lee Bergstrom — chrisleebergstrom.com"
					className="mr-auto inline-flex items-center rounded-sm opacity-70 outline-none transition-opacity duration-200 hover:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
				>
					<span className="sr-only">
						Chris Lee Bergstrom — visit chrisleebergstrom.com (opens in a new tab)
					</span>
					<video
						ref={videoRef}
						src="/videos/animated-logo-trimmed.mp4"
						poster="/images/logo-poster.jpg"
						autoPlay
						loop
						muted
						playsInline
						preload="auto"
						className="h-8 w-auto"
						style={{
							mask: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
							maskComposite: 'intersect',
							WebkitMask:
								'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
							WebkitMaskComposite: 'source-in',
						}}
					/>
				</a>
				{hasAnyInput && (
					<>
						{estimate && (
							<>
								<ShareButton
									origin={origin}
									destination={destination}
									waypoints={waypoints}
									vehicle={vehicleId}
									fuelType={fuelType}
									customMpg={customMpg}
								/>
								<PdfExport
									estimate={estimate}
									originName={origin?.name ?? ''}
									destinationName={destination?.name ?? ''}
								/>
							</>
						)}
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label="Reset all inputs"
							title="Reset all inputs"
							onClick={onReset}
						>
							<RotateCcw className="size-3.5" />
						</Button>
					</>
				)}
				{/* Always visible, far right — the actions above come and go. */}
				<DonateButton />
			</div>
		</div>
	)
}
